import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiEye,
  FiMic,
  FiEdit3,
  FiCalendar,
  FiClock,
  FiTrendingUp,
  FiFilter,
  FiBookOpen,
  FiHeadphones,
  FiActivity,
  FiFileText,
  FiBook,
  FiCheck,
  FiArrowRight,
  FiRefreshCw,
  FiTrash2,
  FiBarChart,
} from 'react-icons/fi';
import InfoTooltip from '../../../components/InfoTooltip';
import Sidebar from '../../Sidebar';
import { getCompletedLessons } from '../../../utils/lessonCompletion';
import EnglishSidebar from './EnglishSidebar';
import '../English.css'; // Import the layout styles
import {
  API_BASE_URL,
  getAccountScopedLocalStorageKey,
  getAuthHeaders,
} from '../../../utils/utils';
import { getStoredUser } from '../../../utils/authSession';
import { ENGLISH_SIDEBAR_CATEGORIES } from '../../../utils/englishCategories';
import {
  LIBRARY_GRAMMAR_LESSONS,
  LIBRARY_VOCABULARY_LESSONS,
} from '../../../utils/ieltsCatalog';

const serverUrl = API_BASE_URL;

const getScopedLocalStorageItem = (key) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  return scopedKey ? localStorage.getItem(scopedKey) : null;
};

const removeScopedLocalStorageItem = (key) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  if (scopedKey) localStorage.removeItem(scopedKey);
};

// Use shared categories from utils
const CATEGORIES = ENGLISH_SIDEBAR_CATEGORIES;

const GRAMMAR_LESSONS = LIBRARY_GRAMMAR_LESSONS;
const VOCABULARY_LESSONS = LIBRARY_VOCABULARY_LESSONS;





const Progress = ({ sidebar }) => {
  const navigate = useNavigate();
  const [userProgress, setUserProgress] = useState([]);
  const [completedLessons, setCompletedLessons] = useState({});
  const [allProgressItems, setAllProgressItems] = useState([]); // Combined tests + lessons
  const [filteredProgress, setFilteredProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all'); // 'all', 'speaking', 'writing', 'reading', 'listening', 'lessons'
  const [selectedCategory, setSelectedCategory] = useState('progression');

  // Add refs to prevent duplicate backend requests
  const backendRequestInProgress = useRef(false);
  const backendRequestCache = useRef(null);
  const cacheTimestamp = useRef(null);

  useEffect(() => {
    loadUserProgress();
    loadCompletedLessons();

    // Listen for progress updates (e.g., when tests are completed)
    const handleProgressUpdate = (event) => {
      console.log('Progress update event received:', event.detail);
      // Clear cache to ensure fresh data
      clearBackendCache();
      // Reload progress with force refresh
      loadUserProgress(true);
      loadCompletedLessons();
    };

    // Listen for test completion events
    window.addEventListener('testCompleted', handleProgressUpdate);
    window.addEventListener('progressUpdate', handleProgressUpdate);

    return () => {
      window.removeEventListener('testCompleted', handleProgressUpdate);
      window.removeEventListener('progressUpdate', handleProgressUpdate);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    combineProgressItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProgress, completedLessons]);

  useEffect(() => {
    applyFilter();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProgressItems, filterType]);

  // eslint-disable-next-line no-unused-vars
  const handleCategoryNavigation = (categoryKey) => {
    if (categoryKey === 'progression') {
      // Already on progression page, just update state
      setSelectedCategory(categoryKey);
    } else {
      // Navigate to main English page with selected category
      navigate('/exams/english', {
        state: {
          activeCategory: categoryKey,
        },
      });
    }
  };

  // Fetch user progress from backend
  const fetchUserProgressFromBackend = async () => {
    try {
      // Check if request is already in progress
      if (backendRequestInProgress.current) {
        console.log('Backend progress request already in progress, waiting...');
        // Wait for the existing request to complete
        while (backendRequestInProgress.current) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        // Return cached result if available
        if (backendRequestCache.current) {
          console.log('Returning cached backend progress result');
          return backendRequestCache.current;
        }
      }

      // Check if we have a recent cached result (within 30 seconds)
      const now = Date.now();
      if (
        backendRequestCache.current &&
        cacheTimestamp.current &&
        now - cacheTimestamp.current < 30000
      ) {
        console.log('Returning cached backend progress result (recent)');
        return backendRequestCache.current;
      }

      // Set loading flag
      backendRequestInProgress.current = true;

      const user = getStoredUser();
      if (!user?.email) {
        throw new Error('User email not found');
      }

      console.log('Fetching user progress from backend...');
      const response = await fetch(
        `${serverUrl}/api/get-user-progress`,
        { headers: getAuthHeaders() }
      );

      if (response.ok) {
        const backendData = await response.json();
        console.log('Backend user progress:', backendData);

        if (backendData.success && backendData.progress) {
          // Cache the result
          backendRequestCache.current = backendData.progress;
          cacheTimestamp.current = now;
          return backendData.progress;
        } else {
          throw new Error('No progress found in backend response');
        }
      } else {
        throw new Error(`Backend request failed: ${response.status}`);
      }
    } catch (error) {
      console.log(
        'Backend progress not found, using localStorage fallback...',
        error
      );
      const fallbackData = loadUserProgressFromLocalStorage();
      // Cache the fallback result too
      backendRequestCache.current = fallbackData;
      cacheTimestamp.current = Date.now();
      return fallbackData;
    } finally {
      // Always clear the loading flag
      backendRequestInProgress.current = false;
    }
  };

  // Function to clear backend cache (useful for forced refresh)
  const clearBackendCache = () => {
    backendRequestCache.current = null;
    cacheTimestamp.current = null;
    backendRequestInProgress.current = false;
    console.log('Backend progress cache cleared');
  };

  // Load user progress from localStorage (fallback)
  const loadUserProgressFromLocalStorage = () => {
    try {
      const progress = JSON.parse(getScopedLocalStorageItem('userProgress') || '[]');
      console.log('Loaded progress data from localStorage:', progress);

      // Filter out any invalid entries and ensure they have the required structure
      const validProgress = progress.filter((item) => {
        const isValid =
          item &&
          item.testId &&
          item.completedAt &&
          (item.results || item.assessmentData) &&
          (item.testType || item.type);

        if (!isValid) {
          console.warn('Invalid progress item:', item);
        }

        return isValid;
      });

      // Sort by completion date, most recent first
      return validProgress.sort(
        (a, b) => new Date(b.completedAt) - new Date(a.completedAt)
      );
    } catch (error) {
      console.error('Error loading progress from localStorage:', error);
      return [];
    }
  };

  const loadUserProgress = async (force = false) => {
    try {
      setLoading(true);
      if (force) {
        clearBackendCache();
      }
      const progressData = await fetchUserProgressFromBackend();
      console.log('Final progress data:', progressData);
      setUserProgress(progressData);
    } catch (error) {
      console.error('Error loading user progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompletedLessons = () => {
    setCompletedLessons(getCompletedLessons());
  };

  // Function to regroup test attempts by task/topic
  const regroupTestAttempts = (testProgress) => {
    const grouped = {};

    testProgress.forEach((item) => {
      const testType = item.testType || item.type || 'speaking';
      const key = `${testType}-${item.topic || item.testTitle || 'unknown'}`;

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    });

    // For each group, keep only the latest attempt (most recent completedAt)
    const regrouped = [];
    Object.values(grouped).forEach((attemptGroup) => {
      if (attemptGroup.length > 0) {
        // Sort by completion date (newest first) and take the first one
        const sortedAttempts = attemptGroup.sort(
          (a, b) => new Date(b.completedAt) - new Date(a.completedAt)
        );
        const latestAttempt = sortedAttempts[0];

        // Add metadata about attempts
        latestAttempt.totalAttempts = attemptGroup.length;
        latestAttempt.allAttempts = sortedAttempts;

        regrouped.push(latestAttempt);
      }
    });

    return regrouped;
  };

  // Function to combine tests and lessons into one progress list
  const combineProgressItems = () => {
    const regroupedTests = regroupTestAttempts(userProgress);

    console.log('Completed lessons:', completedLessons);
    // Get completed grammar lessons
    const completedGrammarLessons = GRAMMAR_LESSONS.filter(
      (lesson) => completedLessons[`grammar-${lesson.id}`]
    ).map((lesson) => ({
      id: `grammar-${lesson.id}`,
      title: lesson.title,
      description: lesson.description,
      path: lesson.path,
      type: 'grammar',
      itemType: 'lesson',
      completedAt:
        completedLessons[`grammar-${lesson.id}`].completedAt ||
        new Date().toISOString(),
      date: completedLessons[`grammar-${lesson.id}`].date || '1 day ago',
    }));

    // Get completed vocabulary lessons
    const completedVocabularyLessons = VOCABULARY_LESSONS.filter(
      (lesson) => completedLessons[`vocabulary-${lesson.id}`]
    ).map((lesson) => ({
      id: `vocabulary-${lesson.id}`,
      title: lesson.title,
      description: lesson.description,
      path: lesson.path,
      type: 'vocabulary',
      itemType: 'lesson',
      completedAt:
        completedLessons[`vocabulary-${lesson.id}`].completedAt ||
        new Date().toISOString(),
      date: completedLessons[`vocabulary-${lesson.id}`].date || '1 day ago',
    }));

    // Combine all progress items
    const combined = [
      ...regroupedTests.map((item) => ({ ...item, itemType: 'test' })),
      ...completedGrammarLessons,
      ...completedVocabularyLessons,
    ];

    // Sort by completion date (newest first)
    combined.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

    setAllProgressItems(combined);
  };

  const applyFilter = () => {
    if (filterType === 'all') {
      setFilteredProgress(allProgressItems);
    } else if (filterType === 'lessons') {
      // Show only grammar and vocabulary lessons
      const filtered = allProgressItems.filter(
        (item) => item.itemType === 'lesson'
      );
      setFilteredProgress(filtered);
    } else {
      const filtered = allProgressItems.filter((item) => {
        const testType = item.testType || item.type || 'speaking'; // Default to speaking for backward compatibility
        return testType === filterType && item.itemType === 'test';
      });
      setFilteredProgress(filtered);
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getScoreColor = (score) => {
    if (!score) return 'var(--liwaza-on-surface-variant)';
    if (score >= 7) return '#059669'; // Green
    if (score >= 5.5) return '#d97706'; // Orange
    return '#dc2626'; // Red
  };

  const getTestIcon = (testType) => {
    switch (testType) {
      case 'speaking':
        return <FiMic style={{ color: 'var(--liwaza-primary)' }} />;
      case 'writing':
        return <FiEdit3 style={{ color: 'var(--liwaza-primary)' }} />;
      case 'reading':
        return <FiBookOpen style={{ color: 'var(--liwaza-primary)' }} />;
      case 'listening':
        return <FiHeadphones style={{ color: 'var(--liwaza-primary)' }} />;
      case 'grammar':
        return <FiFileText style={{ color: 'var(--liwaza-primary)' }} />;
      case 'vocabulary':
        return <FiBook style={{ color: 'var(--liwaza-primary)' }} />;
      default:
        return <FiMic style={{ color: 'var(--liwaza-primary)' }} />;
    }
  };

  const getTestTypeLabel = (testType) => {
    switch (testType) {
      case 'speaking':
        return 'Speaking';
      case 'writing':
        return 'Writing';
      case 'reading':
        return 'Reading';
      case 'listening':
        return 'Listening';
      case 'grammar':
        return 'Grammar';
      case 'vocabulary':
        return 'Vocabulary';
      case 'lessons':
        return 'Lessons';
      default:
        return 'Speaking';
    }
  };

  const handleViewResults = (item) => {
    if (item.itemType === 'lesson') {
      // Navigate to lesson
      navigate(item.path);
      return;
    }

    const testType = item.testType || item.type || 'speaking';

    if (testType === 'speaking') {
      navigate(`/exams/english/speaking-results/${item.testId}`);
      return;
    }

    navigate(`/exams/english/${testType}/results/${item.testId}`);
  };

  const getOverallScore = (item) => {
    const testType = item.testType || item.type || 'speaking';

    if (testType === 'speaking') {
      // Handle different speaking data structures
      if (item.results?.assessment?.overallBandScore !== undefined) {
        return item.results.assessment.overallBandScore;
      } else if (item.results?.overallBandScore !== undefined) {
        return item.results.overallBandScore;
      } else if (
        item.backendResult?.assessment?.overallBandScore !== undefined
      ) {
        return item.backendResult.assessment.overallBandScore;
      }
    } else if (
      testType === 'writing' &&
      item.assessmentData?.overallBandScore
    ) {
      return item.assessmentData.overallBandScore;
    } else if (
      testType === 'writing' &&
      item.assessmentData?.task1?.assessment &&
      item.assessmentData?.task2?.assessment
    ) {
      // Calculate average for writing if no overall score
      const task1Score =
        parseFloat(item.assessmentData.task1.assessment.estimated_band_score) ||
        0;
      const task2Score =
        parseFloat(item.assessmentData.task2.assessment.estimated_band_score) ||
        0;
      return ((task1Score + task2Score) / 2).toFixed(1);
    }

    return 'N/A';
  };

  const getDetailedScores = (item) => {
    const testType = item.testType || item.type || 'speaking';

    if (testType === 'speaking') {
      // Handle different speaking data structures
      let assessment = null;

      if (item.results?.assessment) {
        assessment = item.results.assessment;
      } else if (item.results?.overallBandScore !== undefined) {
        // Direct structure without nested assessment
        assessment = {
          fluencyAndCoherence: item.results.fluencyAndCoherence,
          lexicalResource: item.results.lexicalResource,
          grammaticalRangeAndAccuracy: item.results.grammaticalRangeAndAccuracy,
          pronunciation: item.results.pronunciation,
        };
      } else if (item.backendResult?.assessment) {
        assessment = item.backendResult.assessment;
      }

      if (assessment) {
        return [
          { label: 'Fluency', score: assessment.fluencyAndCoherence },
          { label: 'Vocabulary', score: assessment.lexicalResource },
          { label: 'Grammar', score: assessment.grammaticalRangeAndAccuracy },
          { label: 'Pronunciation', score: assessment.pronunciation },
        ].filter((item) => item.score !== undefined && item.score !== null);
      }
    } else if (testType === 'writing') {
      // For writing tests, we can show task scores or component scores
      if (
        item.assessmentData?.task1?.assessment &&
        item.assessmentData?.task2?.assessment
      ) {
        return [
          {
            label: 'Task 1',
            score:
              parseFloat(
                item.assessmentData.task1.assessment.estimated_band_score
              ) || 0,
          },
          {
            label: 'Task 2',
            score:
              parseFloat(
                item.assessmentData.task2.assessment.estimated_band_score
              ) || 0,
          },
        ];
      }
    }

    return [];
  };

  if (loading) {
    return (
      <div className="english-layout">
        {sidebar && <Sidebar />}
        <main className="english-main">
          <div className="layout-loading">
            <div className="loading-spinner"></div>
            <p>Loading your progress...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="english-layout">
      <Sidebar />
      <main className="english-main">
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <div className="progress-header" style={{ marginBottom: '2rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem',
              }}
            >
              <div>
                <h1
                  style={{
                    color: 'var(--liwaza-primary)',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <FiActivity size={28} /> My Progress
                </h1>
                <p
                  style={{
                    color: 'var(--liwaza-on-surface-variant)',
                    margin: 0,
                  }}
                >
                  Track your IELTS practice sessions and improvements over time
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    clearBackendCache();
                    loadUserProgress(true);
                    loadCompletedLessons();
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid var(--liwaza-outline)',
                    background: 'var(--liwaza-surface-container)',
                    color: 'var(--liwaza-on-surface)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <FiRefreshCw size={16} /> Refresh
                </button>
                {/* Debug button - only show in development */}
                {process.env.NODE_ENV === 'development' && (
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          'This will clear all your progress data. Are you sure?'
                        )
                      ) {
                        removeScopedLocalStorageItem('userProgress');
                        loadUserProgress();
                      }
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid var(--liwaza-error)',
                      background: 'var(--liwaza-error-container)',
                      color: 'var(--liwaza-on-error-container)',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <FiTrash2 size={16} /> Clear Data
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filter Controls */}
          {allProgressItems.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: '2rem',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <FiFilter
                  size={20}
                  style={{ color: 'var(--liwaza-on-surface-variant)' }}
                />
                <span
                  style={{
                    color: 'var(--liwaza-on-surface-variant)',
                    fontWeight: '500',
                  }}
                >
                  Filter:
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[
                  'all',
                  'speaking',
                  'writing',
                  'reading',
                  'listening',
                  'lessons',
                ].map((type) => (
                  <button
                    key={type}
                    onClick={() => setFilterType(type)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '100px',
                      border: 'none',
                      background:
                        filterType === type
                          ? 'var(--liwaza-primary)'
                          : 'var(--liwaza-surface-container)',
                      color:
                        filterType === type
                          ? 'var(--liwaza-on-primary)'
                          : 'var(--liwaza-on-surface)',
                      cursor: 'pointer',
                      fontWeight: '500',
                      textTransform: 'capitalize',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {type}{' '}
                    {type === 'all'
                      ? `(${allProgressItems.length})`
                      : type === 'lessons'
                        ? `(${allProgressItems.filter((item) => item.itemType === 'lesson').length})`
                        : `(${allProgressItems.filter((item) => (item.testType || item.type || 'speaking') === type && item.itemType === 'test').length})`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {filteredProgress.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '3rem',
                background: 'var(--liwaza-surface-container)',
                borderRadius: '12px',
              }}
            >
              <FiTrendingUp
                size={48}
                style={{ color: 'var(--liwaza-primary)', marginBottom: '1rem' }}
              />
              <h2
                style={{ color: 'var(--liwaza-primary)', marginBottom: '1rem' }}
              >
                {filterType === 'all'
                  ? 'No Practice Sessions Yet'
                  : `No ${getTestTypeLabel(filterType)} Sessions Yet`}
              </h2>
              <p
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  marginBottom: '1.5rem',
                }}
              >
                {filterType === 'all'
                  ? 'Complete your first practice to start tracking your progress!'
                  : `Complete your first ${filterType} practice to see results here!`}
              </p>
              <button
                className="btn-lwz-primary"
                onClick={() => navigate('/exams/english')}
                style={{ padding: '0.75rem 2rem' }}
              >
                Start Practice
              </button>
            </div>
          ) : (
            <div className="progress-list">
              <div
                style={{
                  display: 'grid',
                  gap: '1rem',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                }}
              >
                {filteredProgress.map((item, index) => {
                  const testType = item.testType || item.type || 'speaking';
                  const isLesson = item.itemType === 'lesson';
                  const overallScore = isLesson ? null : getOverallScore(item);
                  const detailedScores = isLesson
                    ? []
                    : getDetailedScores(item);

                  return (
                    <div
                      key={index}
                      style={{
                        background: 'var(--liwaza-surface-container)',
                        padding: '1.5rem',
                        borderRadius: '12px',
                        border: '1px solid var(--liwaza-outline-variant)',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%', // Ensure all cards have same height in grid
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow =
                          '0 4px 12px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {/* Header */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '1rem',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {getTestIcon(testType)}
                            <h3
                              style={{
                                color: 'var(--liwaza-primary)',
                                margin: 0,
                                fontSize: '1.1rem',
                              }}
                            >
                              {isLesson
                                ? item.title
                                : item.testTitle ||
                                  `${getTestTypeLabel(testType)} Practice`}
                            </h3>
                            {isLesson && (
                              <span
                                style={{
                                  background: 'var(--liwaza-success)',
                                  color: 'white',
                                  padding: '0.125rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                }}
                              >
                                <FiCheck size={12} /> Completed
                              </span>
                            )}
                          </div>
                          <p
                            style={{
                              color: 'var(--liwaza-on-surface-variant)',
                              margin: 0,
                              fontSize: '0.9rem',
                            }}
                          >
                            {isLesson
                              ? item.description
                              : item.topic || 'Practice Session'}
                          </p>
                          {!isLesson &&
                            item.totalAttempts &&
                            item.totalAttempts > 1 && (
                              <p
                                style={{
                                  color: 'var(--liwaza-primary)',
                                  margin: '0.25rem 0 0 0',
                                  fontSize: '0.8rem',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                }}
                              >
                                <FiBarChart size={14} /> {item.totalAttempts}{' '}
                                attempts (showing latest)
                              </p>
                            )}
                        </div>
                        {!isLesson && (
                          <div
                            style={{
                              background:
                                typeof overallScore === 'number'
                                  ? getScoreColor(overallScore)
                                  : 'var(--liwaza-on-surface-variant)',
                              color: 'white',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.9rem',
                              fontWeight: 'bold',
                            }}
                          >
                            {overallScore !== 'N/A'
                              ? `${overallScore}/9`
                              : 'N/A'}
                          </div>
                        )}
                      </div>

                      {/* Content Area - Flex grow to fill space */}
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {/* Detailed Scores - only for tests */}
                        {!isLesson && detailedScores.length > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${Math.min(detailedScores.length, 2)}, 1fr)`,
                                gap: '0.5rem',
                                fontSize: '0.85rem',
                              }}
                            >
                              {detailedScores.map((scoreItem, scoreIndex) => (
                                <div
                                  key={scoreIndex}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <span
                                    style={{
                                      color: 'var(--liwaza-on-surface-variant)',
                                    }}
                                  >
                                    {scoreItem.label}:
                                  </span>
                                  <span
                                    style={{
                                      color: 'var(--liwaza-primary)',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {scoreItem.score !== undefined &&
                                    scoreItem.score !== null
                                      ? `${scoreItem.score}/9`
                                      : 'N/A'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Spacer to push content to bottom */}
                        <div style={{ flexGrow: 1 }}></div>

                        {/* Session Info */}
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingTop: '1rem',
                            borderTop:
                              '1px solid var(--liwaza-outline-variant)',
                            fontSize: '0.85rem',
                            color: 'var(--liwaza-on-surface-variant)',
                            marginTop: 'auto', // Push to bottom if no other content
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                            }}
                          >
                            <FiCalendar size={14} />
                            {formatDate(item.completedAt)}
                          </div>
                          {!isLesson && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                              }}
                            >
                              <FiClock size={14} />
                              {formatDuration(item.duration)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Button - Always at bottom */}
                      <button
                        className="btn-lwz-primary"
                        onClick={() => handleViewResults(item)}
                        style={{
                          marginTop: '1rem',
                          padding: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          flexShrink: 0, // Prevent button from shrinking
                        }}
                      >
                        {isLesson ? (
                          <>
                            <FiArrowRight /> Review Lesson
                          </>
                        ) : (
                          <>
                            <FiEye /> View Results
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary Stats */}
          {filteredProgress.length > 0 && (
            <div
              style={{
                marginTop: '2rem',
                padding: '1.5rem',
                background: 'var(--liwaza-primary-container)',
                borderRadius: '12px',
              }}
            >
              <h3
                style={{
                  color: 'var(--liwaza-on-primary-container)',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiTrendingUp size={20} />{' '}
                {filterType === 'all'
                  ? 'Overall'
                  : getTestTypeLabel(filterType)}{' '}
                Progress Summary
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      color: 'var(--liwaza-on-primary-container)',
                    }}
                  >
                    {filteredProgress.length}
                  </div>
                  <div
                    style={{
                      color: 'var(--liwaza-on-primary-container)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    {filterType === 'lessons'
                      ? 'Completed Lessons'
                      : filterType === 'all'
                        ? 'Total Items'
                        : 'Practice Sessions'}
                    <InfoTooltip
                      content={
                        filterType === 'lessons'
                          ? "Number of grammar and vocabulary lessons you've completed. Each lesson builds essential language skills for IELTS success."
                          : filterType === 'all'
                            ? 'Total practice sessions and lessons completed across all IELTS sections. More practice leads to better scores and confidence.'
                            : `Number of ${filterType} practice sessions completed. Regular practice in each section is essential for IELTS band score improvement.`
                      }
                      position="bottom"
                      maxWidth="280px"
                    />
                  </div>
                </div>
                {filterType !== 'lessons' && (
                  <>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '2rem',
                          fontWeight: 'bold',
                          color: 'var(--liwaza-on-primary-container)',
                        }}
                      >
                        {(() => {
                          const testItems = filteredProgress.filter(
                            (item) => item.itemType === 'test'
                          );
                          const validScores = testItems
                            .map((item) => getOverallScore(item))
                            .filter(
                              (score) =>
                                typeof score === 'number' && !isNaN(score)
                            );

                          if (validScores.length === 0) return 'N/A';

                          const average =
                            validScores.reduce(
                              (sum, score) => sum + parseFloat(score),
                              0
                            ) / validScores.length;
                          return average.toFixed(1);
                        })()}
                      </div>
                      <div
                        style={{
                          color: 'var(--liwaza-on-primary-container)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        Average Test Score
                        <InfoTooltip
                          content="Your average IELTS band score across all practice tests. This shows your consistent performance level. Aim for consistent 6.5+ scores to demonstrate readiness for most universities."
                          position="bottom"
                          maxWidth="280px"
                        />
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: '2rem',
                          fontWeight: 'bold',
                          color: 'var(--liwaza-on-primary-container)',
                        }}
                      >
                        {(() => {
                          const testItems = filteredProgress.filter(
                            (item) => item.itemType === 'test'
                          );
                          const validScores = testItems
                            .map((item) => getOverallScore(item))
                            .filter(
                              (score) =>
                                typeof score === 'number' && !isNaN(score)
                            );

                          if (validScores.length === 0) return 'N/A';

                          return Math.max(
                            ...validScores.map((score) => parseFloat(score))
                          ).toFixed(1);
                        })()}
                      </div>
                      <div
                        style={{
                          color: 'var(--liwaza-on-primary-container)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        Best Test Score
                        <InfoTooltip
                          content="Your highest IELTS band score achieved in practice tests. This represents your peak performance potential. Use this to gauge your target score and track improvement over time."
                          position="bottom"
                          maxWidth="280px"
                        />
                      </div>
                    </div>
                  </>
                )}
                {filterType === 'all' && (
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        color: 'var(--liwaza-on-primary-container)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      Completed Lessons
                      <InfoTooltip
                        content="Number of grammar and vocabulary lessons you've completed. These lessons build fundamental English skills essential for high IELTS band scores. Completing more lessons improves your language foundation."
                        position="bottom"
                        maxWidth="280px"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      {sidebar && (
        <EnglishSidebar
          categories={CATEGORIES}
          selectedCategory={selectedCategory}
          setSelectedCategory={(category) => setSelectedCategory(category)}
        />
      )}
    </div>
  );
};

export default Progress;
